import { GameConfig } from "@/config/GameConfig";
import type { Point } from "@/core/types";
import type { LevelDefinition } from "./types";
import { cellKey, horizontalWall, mergeUnique, rect, verticalWall } from "./geometry";

// Same tightened 18x18 board as world 1 - EARTH keeps the exact board
// size, just raises the difficulty through obstacle shape (more turns,
// tighter corridors, longer required growth), never through a new
// mechanic or a changed balance constant.
const GRID = { cols: 18, rows: 18 };

/** Removes duplicate cells from a border/perimeter outline. */
function rectBorder(xFrom: number, yFrom: number, xTo: number, yTo: number): Point[] {
  return mergeUnique(horizontalWall(yFrom, xFrom, xTo), horizontalWall(yTo, xFrom, xTo), verticalWall(xFrom, yFrom, yTo), verticalWall(xTo, yFrom, yTo));
}

function withoutCells(points: Point[], remove: Point[]): Point[] {
  const removeKeys = new Set(remove.map(cellKey));
  return points.filter((p) => !removeKeys.has(cellKey(p)));
}

/**
 * A two-cell-thick diagonal staircase spanning the full board height -
 * FAULT LINE's "crack". Consecutive rows always share at least one column
 * so there's no diagonal leak between them; `gapRows` are left completely
 * open, each one a full crossing between the two sides.
 */
function faultLineWall(gapRows: ReadonlySet<number>): Point[] {
  const points: Point[] = [];
  for (let y = 0; y < GRID.rows; y++) {
    if (gapRows.has(y)) continue;
    const xStart = 2 + Math.floor(y / 2);
    points.push({ x: xStart, y }, { x: xStart + 1, y });
  }
  return points;
}

/** 2x2 "buildings" laid out on a repeating grid, leaving a uniform 2-wide street both ways - CITY GRID's blocks. */
function cityBlocks(anchors: number[]): Point[] {
  const groups: Point[][] = [];
  for (const bx of anchors) {
    for (const by of anchors) {
      groups.push(rect(bx, by, bx + 1, by + 1));
    }
  }
  return mergeUnique(...groups);
}

export const WORLD_2_LEVELS: LevelDefinition[] = [
  {
    id: "w2-l1",
    name: "BLUE PLANET",
    worldId: "w2",
    levelInWorld: 1,
    order: 6,
    grid: GRID,
    snakeStart: { x: 2, y: 2 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 19,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w2,
    // Three generously-spaced 3x3 "islands" - wide open water between and
    // around every one of them, at least two ways around any of them.
    obstacles: mergeUnique(rect(3, 3, 5, 5), rect(9, 7, 11, 9), rect(13, 12, 15, 14)),
    exit: { x: 16, y: 16 },
    pace: { paceParMs: 89000, pacePointsPerSecond: 2, maxPaceBonus: 48 },
  },
  {
    id: "w2-l2",
    name: "THE RIVER",
    worldId: "w2",
    levelInWorld: 2,
    order: 7,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 21,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w2,
    // A winding two-cell-wide river running top to bottom, drifting one
    // column east every third of the board (cols 7-8 -> 8-9 -> 9-10) so
    // it reads as a bend, not a straight wall. Three crossings, each a
    // full open row, spread across different rows (3, 9, 15) so no two
    // are on the same line.
    // PR28 WORMHOLE edge-safety fix: the drift itself means the top row
    // (y=0, cols 7-8) and bottom row (y=17, cols 9-10) never occupied the
    // same columns, so a vertical WORMHOLE wrap at either extreme edge
    // could land inside the river on one side but not the other. Sealed
    // both edges to the river's full 4-column drift range (7-10) by
    // adding the two cells each edge was missing - never removes an
    // existing block, and never touches the three designed crossing rows
    // (3, 9, 15), so the forced-crossing design is unchanged.
    obstacles: withoutCells(
      mergeUnique(
        rect(7, 0, 8, 5),
        rect(8, 6, 9, 11),
        rect(9, 12, 10, 17),
        rect(9, 0, 10, 0),
        rect(7, 17, 8, 17),
      ),
      [3, 9, 15].flatMap((y) => [
        { x: 7, y },
        { x: 8, y },
        { x: 9, y },
        { x: 10, y },
      ]),
    ),
    exit: { x: 15, y: 9 },
    pace: { paceParMs: 94000, pacePointsPerSecond: 2, maxPaceBonus: 54 },
  },
  {
    id: "w2-l3",
    name: "CITY GRID",
    worldId: "w2",
    levelInWorld: 3,
    order: 8,
    grid: GRID,
    snakeStart: { x: 2, y: 1 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 23,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w2,
    // A regular 4x4 grid of 2x2 "buildings" with a uniform 2-wide street
    // network between and around them - frequent turns, several routes
    // between any two points, never a single-cell-wide corridor.
    obstacles: cityBlocks([2, 6, 10, 14]),
    exit: { x: 17, y: 17 },
    pace: { paceParMs: 99000, pacePointsPerSecond: 2, maxPaceBonus: 60 },
  },
  {
    id: "w2-l4",
    name: "FAULT LINE",
    worldId: "w2",
    levelInWorld: 4,
    order: 9,
    grid: GRID,
    snakeStart: { x: 0, y: 2 },
    startDirection: "down",
    initialLength: 3,
    targetLength: 25,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w2,
    // A diagonal, stepped crack running the full height of the board.
    // Three crossings (rows 3, 9, 15) - clearly visible full-row gaps,
    // not aligned with each other, forcing the player to travel along
    // the crack to find the next one.
    // PR28 WORMHOLE edge-safety fix: the diagonal drift means the top row
    // (y=0, cols 2-3) and bottom row (y=17, cols 10-11) never occupied
    // the same columns, so a vertical WORMHOLE wrap at either extreme
    // edge could land inside the crack on one side but not the other.
    // Sealed both edges to the crack's full start/end column range by
    // adding the two cells each edge was missing - the formula itself
    // (and its three crossing rows) is untouched, so the diagonal's
    // visual identity is unchanged.
    obstacles: mergeUnique(faultLineWall(new Set([3, 9, 15])), [
      { x: 10, y: 0 },
      { x: 11, y: 0 },
      { x: 2, y: 17 },
      { x: 3, y: 17 },
    ]),
    exit: { x: 17, y: 17 },
    pace: { paceParMs: 110000, pacePointsPerSecond: 2, maxPaceBonus: 66 },
  },
  {
    id: "w2-l5",
    name: "THE CORE",
    worldId: "w2",
    levelInWorld: 5,
    order: 10,
    grid: GRID,
    snakeStart: { x: 2, y: 2 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 27,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w2,
    // Two broken rings (never closed) around the board's center, each
    // with one 2-wide opening on an alternating side - south on the
    // outer ring, north on the inner one - so reaching the exit means
    // spiralling in through both layers, never in a straight line.
    obstacles: mergeUnique(
      withoutCells(rectBorder(3, 3, 14, 14), [
        { x: 8, y: 14 },
        { x: 9, y: 14 },
      ]),
      withoutCells(rectBorder(7, 7, 10, 10), [
        { x: 8, y: 7 },
        { x: 9, y: 7 },
      ]),
    ),
    exit: { x: 8, y: 8 },
    pace: { paceParMs: 128000, pacePointsPerSecond: 2, maxPaceBonus: 72 },
  },
];
